import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Mounts a single IntersectionObserver that watches every element with
 * [data-reveal] in the DOM and toggles `.is-visible` when it enters view.
 * Re-scans on route change so client-side navigation keeps working.
 */
export default function ScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    const scan = () => {
      document.querySelectorAll("[data-reveal]:not(.is-visible)").forEach((el) => io.observe(el));
    };

    // Initial scan + a tiny delay to catch lazily-rendered children
    scan();
    const t = setTimeout(scan, 120);

    // Observe DOM mutations so late-mounted nodes also animate
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      mo.disconnect();
      io.disconnect();
    };
  }, [location.pathname]);

  return null;
}
